import React, { useState, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';

interface StatusChange {
    id: number;
    fromStatus: { id: number; name: string } | null;
    toStatus: { id: number; name: string };
    user: { id: number; name: string };
    comment: string | null;
    createdAt: string;
    createdAtRelative: string;
}

interface MeasureStatusHistoryProps {
    measureId: number;
    measuresActions: any;
}

const STATUS_COLORS: Record<number, string> = {
    1: '#3b82f6', // Initial — blue
    2: '#22c55e', // Final — green
    3: '#f59e0b', // Pending — amber
};

const STATUS_BG: Record<number, string> = {
    1: '#dbeafe',
    2: '#dcfce7',
    3: '#fef3c7',
};

// Map status id -> i18n key so the badge label re-translates with locale.
// The backend also sends a `name` on each status, but that comes from the DB in
// Albanian; using the key here guarantees the current locale's translation.
const STATUS_I18N_KEY: Record<number, string> = {
    1: 'measure.status.initial',
    2: 'measure.status.final',
    3: 'measure.status.pending',
};

const StatusBadge: React.FC<{ statusId: number; fallback?: string }> = ({ statusId, fallback }) => {
    const color = STATUS_COLORS[statusId] || '#64748b';
    const bg = STATUS_BG[statusId] || '#f1f5f9';
    const key = STATUS_I18N_KEY[statusId];

    return (
        <span className="msh-badge" style={{ color, background: bg }}>
            {key
                ? <FormattedMessage id={key} defaultMessage={fallback || '—'} />
                : (fallback || '—')}
        </span>
    );
};

const MeasureStatusHistory: React.FC<MeasureStatusHistoryProps> = ({ measureId, measuresActions }) => {
    const [history, setHistory] = useState<StatusChange[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (measureId) {
            setLoading(true);
            measuresActions.getMeasureStatusHistory(measureId).then((res: any) => {
                if (res?.data) setHistory(res.data);
                setLoading(false);
            });
        }
    }, [measureId]);

    if (loading) return (
        <div className="msh-empty">
            <FormattedMessage id="measure.history.loading" defaultMessage="Loading..." />
        </div>
    );
    if (history.length === 0) return (
        <div className="msh-empty">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.5">
                <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
            </svg>
            <span><FormattedMessage id="measure.history.noChanges" defaultMessage="No status changes" /></span>
        </div>
    );

    return (
        <div className="msh-list">
            {history.map((entry) => {
                const color = STATUS_COLORS[entry.toStatus.id] || '#94a3b8';

                return (
                    <div key={entry.id} className="msh-item">
                        <div className="msh-item-dot" style={{ background: color }} />
                        <div className="msh-item-content">
                            <div className="msh-item-row">
                                {entry.fromStatus ? (
                                    <div className="msh-status-flow">
                                        <StatusBadge statusId={entry.fromStatus.id} fallback={entry.fromStatus.name} />
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2">
                                            <path d="M5 12h14M12 5l7 7-7 7" />
                                        </svg>
                                        <StatusBadge statusId={entry.toStatus.id} fallback={entry.toStatus.name} />
                                    </div>
                                ) : (
                                    <div className="msh-status-flow">
                                        <span className="msh-created-label">
                                            <FormattedMessage id="measure.history.created" defaultMessage="Created" />
                                        </span>
                                        <StatusBadge statusId={entry.toStatus.id} fallback={entry.toStatus.name} />
                                    </div>
                                )}
                            </div>
                            <div className="msh-item-meta">
                                <div className="msh-item-user">
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" /><circle cx="12" cy="7" r="4" />
                                    </svg>
                                    <span>{entry.user?.name || 'Sistem'}</span>
                                </div>
                                <div className="msh-item-time">
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
                                    </svg>
                                    <span title={entry.createdAt}>{entry.createdAt}</span>
                                </div>
                            </div>
                            {entry.comment && <p className="msh-item-comment">{entry.comment}</p>}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default MeasureStatusHistory;
